import { createAction, props } from '@ngrx/store';
import { OrderType } from 'graphql-api/baskets-api-interfaces';

import { MaxDiscountPercentageValue } from '../../types';
import { ManualDiscountEntity } from '../reducers/basket-manual-discounts.reducer';

export type ApplyBasketManualDiscountsInvalidatedReason =
  | ReturnType<typeof manualDiscountValueBelowCDiscount>
  | ReturnType<typeof maxBasketManualDiscountValueExceeded>;

export function applyBasketManualDiscount({
  basketId,
  value,
  basketItemIds,
}: {
  basketId: string;
  value: number;
  basketItemIds: string[];
}) {
  const values: { [basketItemId: string]: number } = {};
  basketItemIds.forEach((id) => (values[id] = value));

  return applyBasketManualDiscounts({
    basketId,
    values,
  });
}

export const applyBasketManualDiscounts = createAction(
  '[Basket] Apply Basket Manual Discounts',
  props<{
    basketId: string;
    values: { [basketItemId: string]: number };
  }>(),
);

export const applyBasketManualDiscountsValidated = createAction(
  '[Basket] Apply Basket Manual Discounts Validated',
  props<{
    basketId: string;
    applyBasketManualDiscountsAction: ReturnType<
      typeof applyBasketManualDiscounts
    >;
  }>(),
);

export const applyBasketManualDiscountsInvalidated = createAction(
  '[Basket] Apply Basket Manual Discounts Invalidated',
  props<{
    basketId: string;
    reason: ApplyBasketManualDiscountsInvalidatedReason;
  }>(),
);

export const reverseCalculationForDiscountPercentageFromNetTotalValueFailed =
  createAction(
    '[Basket] Reverse Calculation For Discount Percentage From Net Total Value Failed',
    props<{
      message: string;
    }>(),
  );

export const loadBasketManualDiscounts = createAction(
  '[Basket] Load Basket Manual Discounts',
  props<{ basketId: string; entities: ManualDiscountEntity[] }>(),
);

export const manualDiscountValueBelowCDiscount = createAction(
  '[Basket] Manual Discount Value Below CDiscount',
  props<{
    basketId: string;
    invalidated: {
      basketItemId: string;
      cdiscount: number;
      manualDiscount: number;
    }[];
  }>(),
);

export const maxBasketManualDiscountValueExceeded = createAction(
  '[Basket] Max Basket Manual Discount Value Exceeded',
  props<{
    values: {
      [basketItemId: string]: { enteredValue: number; maxValue: number };
    };
  }>(),
);

export const removeBasketManualDiscounts = createAction(
  '[Basket] Remove Basket Manual Discounts',
  props<{
    basketId: string;
    basketItemIds: string[];
  }>(),
);

export const clearBasketManualDiscounts = createAction(
  '[Basket] Clear Basket Manual Discounts',
  props<{
    basketId: string;
    basketItemIds?: string[];
  }>(),
);

export const resetBasketManualDiscountsInputs = createAction(
  '[Basket] Reset Basket Manual Discounts Inputs',
  props<{
    basketId: string;
  }>(),
);

export const loadMaxDiscountPercentage = createAction(
  '[Basket] Load Max Discount Percentage',
);

export const loadMaxDiscountPercentageSuccess = createAction(
  '[Basket] Load Max Discount Percentage Success',
  props<{ maxDiscountPercentageValue: MaxDiscountPercentageValue }>(),
);

export const loadMaxDiscountPercentageForMaterials = createAction(
  '[Basket] Load Max Discount Percentage For Materials',
  props<{
    materialIdsByOrderType: Partial<{ [orderType in OrderType]: string[] }>;
  }>(),
);

export const loadMaxDiscountPercentageForMaterialsSuccess = createAction(
  '[Basket] Load Max Discount Percentage For Materials Success',
  props<{
    discountPercentages: Partial<{
      [orderType in OrderType]: { [materialId: string]: number };
    }>;
  }>(),
);
